import { SectionHeadingContainer } from '@/components/SectionHeadingContainer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const reasons = [
  {
    num: '01',
    title: 'ゼロからでも成長できる環境',
    desc: '段階的なキャリアアップ制度でゼロからでも着実に力を身につけていくことができます。一人ひとりに合わせたサポート体制のもと、無理なく成長できる環境を整えています。',
  },
  {
    num: '02',
    title: '現場で磨かれる本物の力',
    desc: '座学だけではなく、実際の現場で経験を積みながら成長していきます。成果に直結するスキルを身につけ、“できる自分”を実感できる環境です。',
  },
  {
    num: '03',
    title: '人生を自分で選べる未来',
    desc: '営業力・育成力・マーケティング力を武器に、選択肢のある人生を実現していきます。自分の人生を、自分でデザインできる力を身につけていきます。',
  },
];

const itemDelays = ['delay-3', 'delay-3', 'delay-4'] as const;

export default function WhySection() {
  const ref = useScrollAnimation();

  return (
    <section
      id="why"
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: '#0B1A2A', paddingTop: '65px', paddingBottom: '140px' }}
      aria-label="選ばれる理由"
    >
      {/* 左端の縦ライン（エントリーと同系・ダーク用にコントラスト調整） */}
      <div
        className="pointer-events-none absolute left-0 top-0 bottom-0 w-px opacity-50"
        style={{
          background:
            'linear-gradient(180deg, transparent 0%, rgba(168,205,217,0.45) 20%, rgba(168,205,217,0.45) 80%, transparent 100%)',
        }}
        aria-hidden={true}
      />

      {/* 背景：同心円・巨大タイポは廃止し、軽いグローのみ */}
      <div
        className="pointer-events-none absolute -top-28 left-1/2 h-[420px] w-[min(100vw,820px)] -translate-x-1/2 rounded-full opacity-35 blur-3xl"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(61, 107, 140, 0.38) 0%, transparent 72%)',
        }}
        aria-hidden={true}
      />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-8 lg:px-16">
        <div className="fade-in-up mb-6">
          <span
            className="section-label-en font-montserrat font-light uppercase"
            style={{ color: '#A8CDD9' }}
          >
            WHY UNIVERSE
          </span>
        </div>

        {/* 見出し：2行構成・行間は index.css .section-heading--why（タブレット基準で全幅統一） */}
        <SectionHeadingContainer>
          <h2 className="section-heading section-heading--why fade-in-up delay-1 mb-12 text-white lg:mb-14">
            <span>「できるようになる」だけではない、</span>
            <span>自走できる力を身につける。</span>
          </h2>
        </SectionHeadingContainer>

        {/* 3カラム：事業内容セクションと同じリズム（ダーク向け配色） */}
        <div className="grid grid-cols-1 gap-12 border-t border-solid pt-12 sm:gap-14 md:grid-cols-3 md:gap-10 md:border-t-0 md:pt-0 lg:gap-12">
          {reasons.map((reason, i) => (
            <article
              key={reason.num}
              className={`fade-in-up flex min-w-0 flex-col items-center border-b border-solid pb-12 last:border-b-0 last:pb-0 md:items-start md:border-b-0 md:pb-0 md:text-left ${itemDelays[i] ?? 'delay-4'} rounded-lg px-1 py-2 transition-colors md:px-0`}
              style={{
                borderColor: 'rgba(168, 205, 217, 0.22)',
              }}
            >
              <span
                className="font-montserrat text-3xl font-light tabular-nums leading-none sm:text-4xl"
                style={{ color: '#B89B4A', letterSpacing: '-0.02em' }}
                aria-hidden={true}
              >
                {reason.num}
              </span>

              <h3
                className="mt-6 mb-3 font-light text-white"
                style={{
                  fontSize: 'clamp(15px, 1.6vw, 19px)',
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontWeight: 300,
                  letterSpacing: '0.06em',
                }}
              >
                {reason.title}
              </h3>
              <p
                className="max-w-sm font-light leading-[2] md:max-w-none"
                style={{
                  color: 'rgba(255, 255, 255, 0.58)',
                  fontSize: 'clamp(13px, 1.15vw, 15px)',
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontWeight: 300,
                }}
              >
                {reason.desc}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
